import { AlertTriangle, Check, Circle, Copy, RefreshCw } from 'lucide-react';
import ViewModal from '../../../components/Modals/ViewModal';
import InfoField from '../../../components/Shared/InfoField';
import SectionInfoCard from '../../../components/FormSections/SectionInfoCard';
import {
  ContingencyBatch,
  ContingencyDocumentResult,
  ContingencyEventDetail,
} from '../../../types/dte/contingency/ContingencyEventDetail';
import { contingencySteps, reachedContingencyStep, safeParseJson } from '../../../features/contingency/contingencyDetail';

interface Props {
  isOpen: boolean;
  data: ContingencyEventDetail | null;
  errorMessage?: string | null;
  onClose: () => void;
}

const JsonPanel = ({ title, raw }: { title: string; raw?: string | null }) => {
  if (!raw) return null;
  const parsed = safeParseJson(raw);
  const formatted = parsed ? JSON.stringify(parsed, null, 2) : raw;
  return (
    <details className="rounded-lg border border-stroke p-3 dark:border-strokedark">
      <summary className="cursor-pointer font-medium">{title}</summary>
      <button type="button" onClick={() => navigator.clipboard?.writeText(raw)} className="mt-3 inline-flex items-center gap-1 rounded bg-primary px-2 py-1 text-xs text-white">
        <Copy className="h-3 w-3" /> Copiar contenido crudo
      </button>
      <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap break-all rounded bg-gray-2 p-3 text-xs dark:bg-meta-4">{formatted}</pre>
    </details>
  );
};

const text = (...values: Array<string | number | null | undefined>) =>
  values.find((value) => value !== null && value !== undefined && String(value).trim() !== '') ?? '—';

const observations = (row: ContingencyDocumentResult) => {
  const value = row.observations ?? row.observaciones;
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const ResultTable = ({ title, rows }: { title: string; rows?: ContingencyDocumentResult[] | null }) => (
  <section className="min-w-0">
    <h5 className="mb-2 font-semibold">{title} ({rows?.length ?? 0})</h5>
    {!rows?.length ? <p className="rounded-lg bg-gray-2 p-3 text-sm text-body dark:bg-meta-4">Sin documentos.</p> : (
      <div className="overflow-x-auto rounded-lg border border-stroke dark:border-strokedark">
        <table className="w-full min-w-[850px] text-left text-xs">
          <thead className="bg-gray-2 dark:bg-meta-4"><tr><th className="p-2">Código de generación</th><th className="p-2">Estado</th><th className="p-2">Sello</th><th className="p-2">Código Hacienda</th><th className="p-2">Mensaje / observaciones</th></tr></thead>
          <tbody>{rows.map((row, index) => {
            const items = observations(row);
            return (
              <tr key={`${text(row.generationCode, row.codigoGeneracion)}-${row.index ?? index}`} className="border-t border-stroke align-top dark:border-strokedark">
                <td className="break-all p-2">{text(row.generationCode, row.codigoGeneracion)}</td>
                <td className="p-2">{text(row.status, row.estado)}</td>
                <td className="break-all p-2">{text(row.seal, row.sello, row.selloRecibido)}</td>
                <td className="p-2">{text(row.haciendaCode, row.code, row.codigoMsg)}</td>
                <td className="p-2">{text(row.haciendaMessage, row.message, row.descripcionMsg)}{items.length ? <ul className="mt-1 list-disc pl-4">{items.map((item, itemIndex) => <li key={`${item}-${itemIndex}`}>{item}</li>)}</ul> : null}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>
    )}
  </section>
);

const BatchCard = ({ batch }: { batch: ContingencyBatch }) => (
  <article className="space-y-4 rounded-xl border border-stroke p-4 dark:border-strokedark">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div><h4 className="font-bold">Lote {batch.loteCode || '—'}</h4><p className="text-xs text-body">ID: {batch.id}</p></div>
      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${batch.status === 'RECHAZADO' ? 'bg-danger/10 text-danger' : 'bg-primary/10 text-primary'}`}>{batch.status || 'Sin estado'}</span>
    </div>
    <div className="grid gap-2 text-sm sm:grid-cols-2">
      <p>Procesado: {batch.processedAt || '—'}</p>
      <p className={batch.active ? 'font-semibold text-warning' : 'text-success'}>{batch.active ? 'Activo: el proceso continúa o será reintentado' : 'Inactivo: resultado aplicado'}</p>
    </div>
    {batch.processingError && <div role="alert" className="flex gap-2 rounded-lg border border-warning bg-warning/10 p-3 text-sm"><AlertTriangle className="h-5 w-5 shrink-0" /><div><strong>Proceso incompleto</strong><p>{batch.processingError}</p><p className="mt-1 text-xs">Los documentos procesados correctamente se conservan debajo.</p></div></div>}
    <div className="grid gap-5 xl:grid-cols-2">
      <ResultTable title="Documentos procesados" rows={batch.processedDocuments} />
      <ResultTable title="Documentos rechazados" rows={batch.rejectedDocuments} />
    </div>
    <div className="space-y-2">
      <JsonPanel title="Documentos firmados" raw={batch.signedDocuments} />
      <JsonPanel title="Respuesta inicial del lote" raw={batch.haciendaResponse} />
      <JsonPanel title="Auditoría: queryResponse" raw={batch.queryResponse} />
    </div>
  </article>
);

const ContingencyEventDetailModal = ({ isOpen, data, errorMessage, onClose }: Props) => {
  if (!data) return null;
  const finished = data.status === 'FINISHED';

  return (
    <ViewModal popupOpen={isOpen} setPopupOpen={onClose} title="Detalle del Evento de Contingencia" maxWidth="max-w-6xl">
      <div className="space-y-5">
        <div className={`flex items-center gap-2 rounded-lg border p-3 text-sm ${finished ? 'border-success bg-success/10 text-success' : 'border-primary bg-primary/10 text-primary'}`}>
          {finished ? <Check className="h-5 w-5" /> : <RefreshCw className="h-5 w-5 animate-spin" />}
          <strong>{finished ? 'Proceso finalizado' : 'Proceso activo; el detalle se actualiza automáticamente'}</strong>
        </div>
        {errorMessage && <div role="alert" className="flex gap-2 rounded-lg border border-warning bg-warning/10 p-3 text-sm"><AlertTriangle className="h-5 w-5 shrink-0" /><div><strong>Actualización temporalmente no disponible</strong><p>{errorMessage}</p></div></div>}
        <SectionInfoCard title="Información general" columns={2}>
          <InfoField label="Módulo" value={data.module ?? '—'} /><InfoField label="Sucursal" value={data.branchId ?? '—'} />
          <InfoField label="Estado" value={data.status} /><InfoField label="Documentos generados" value={data.documentsGenerated ?? 0} />
          <InfoField label="Documentos transmitidos" value={data.documentsTransmitted ?? 0} /><InfoField label="Progreso" value={`${data.documentsTransmitted ?? 0} / ${data.documentsGenerated ?? 0}`} />
          <InfoField label="Inicio" value={data.startTimestamp} /><InfoField label="Fin" value={data.endTimestamp ?? '—'} />
        </SectionInfoCard>
        <section>
          <h3 className="mb-3 font-bold">Línea de progreso</h3>
          <ol className="grid gap-2 md:grid-cols-3">{contingencySteps.map((step) => {
            const reached = reachedContingencyStep(data.status, step.status);
            return <li key={step.status} className={`flex items-center gap-2 rounded-lg border p-3 text-sm ${reached ? 'border-primary bg-primary/5 text-primary' : 'border-stroke text-body dark:border-strokedark'}`}>{reached ? <Check className="h-4 w-4 shrink-0" /> : <Circle className="h-4 w-4 shrink-0" />}<span><strong className="block text-xs">{step.status}</strong>{step.label}</span></li>;
          })}</ol>
        </section>
        <div className="space-y-2"><JsonPanel title="Firma del evento" raw={data.signedEvent} /><JsonPanel title="Respuesta del evento de Hacienda" raw={data.haciendaResponse} /></div>
        <section className="space-y-3"><h3 className="font-bold">Lotes asociados ({data.batches?.length ?? 0})</h3>{data.batches?.length ? data.batches.map((batch) => <BatchCard key={batch.id} batch={batch} />) : <p className="rounded-lg bg-gray-2 p-4 text-sm text-body dark:bg-meta-4">Todavía no hay lotes asociados. La información del evento permanece visible mientras el proceso continúa.</p>}</section>
        <button onClick={onClose} className="w-full rounded-md bg-primary px-4 py-2 text-white">Cerrar</button>
      </div>
    </ViewModal>
  );
};

export default ContingencyEventDetailModal;
